import { useContext, useEffect, useState } from 'react';
import {
  ContextServiceContent,
  ContextLocaleService,
  ContextVueFormatter,
} from './components/Providers';
import { getPerSlideWltpCompliance } from './wltpCompliance';
import type { Logger } from './feature-service-types';

export interface UseWltpComplianceGateArgs {
  isMarketComplianceEnabled?: boolean;
  initialCompliance?: boolean[];
  logger?: Pick<Logger, 'warn' | 'error'>;
}

export const useWltpComplianceGate = ({
  isMarketComplianceEnabled,
  initialCompliance,
  logger,
}: UseWltpComplianceGateArgs): boolean[] | null | undefined => {
  const content = useContext(ContextServiceContent);
  const localeService = useContext(ContextLocaleService);
  const vueFormatterService = useContext(ContextVueFormatter);

  const isGateActive = Boolean(isMarketComplianceEnabled);

  const [clientCompliance, setClientCompliance] = useState<boolean[] | undefined>(undefined);

  useEffect(() => {
    if (!isGateActive) return undefined;
    if (!content || !localeService || !vueFormatterService) return undefined;

    const controller = new AbortController();
    const runCheck = async () => {
      const slideCompliance = await getPerSlideWltpCompliance(
        content.slides,
        vueFormatterService,
        localeService,
        logger,
      );
      if (!controller.signal.aborted) {
        setClientCompliance(slideCompliance);
      }
    };
    void runCheck();
    return () => {
      controller.abort();
    };
  }, [isGateActive, content, localeService, vueFormatterService, logger]);

  if (!isGateActive) return undefined;

  const compliance = clientCompliance ?? initialCompliance;
  return compliance ?? null;
};
